import React, { useEffect, useState } from "react";
import { Flame, ShieldCheck, RefreshCw, AlertTriangle, Upload, X } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import api from "../lib/api";
import { toast } from "sonner";

// Tenant-facing Firebase section. Read-only view for auto-provisioned
// slots; BYO uploader for tenants who bring their own Firebase project.
// Explicitly does NOT expose Firebase service-account credentials — that
// remains Super-Admin-only per platform policy.

const SLOT_LABELS = {
  customer_android: "Customer · Android",
  customer_ios: "Customer · iOS",
  rider_android: "Rider · Android",
  rider_ios: "Rider · iOS",
};

const SLOT_KEYS = ["customer_android", "customer_ios", "rider_android", "rider_ios"];

export default function FirebaseSection() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/api/admin/firebase/status");
      setStatus(data);
    } catch (e) {
      toast.error("Failed to load Firebase status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <section className="bg-white rounded-3xl border border-neutral-200 p-6" data-testid="firebase-section">
        <div className="text-neutral-500 text-sm">Loading Firebase status…</div>
      </section>
    );
  }

  const byoCount = SLOT_KEYS.filter((k) => (status?.slots?.[k]?.source) === "byo").length;
  const autoCount = SLOT_KEYS.filter((k) => (status?.slots?.[k]?.source) === "auto").length;
  const saPresent = !!status?.service_account?.present;
  const saMissingForByo = byoCount > 0 && !saPresent;

  return (
    <section className="bg-white rounded-3xl border border-neutral-200 p-6" data-testid="firebase-section">
      <div className="flex items-center gap-3 mb-2">
        <Flame className="w-5 h-5 text-brand-clay" />
        <h2 className="font-heading font-bold text-lg">Native App · Firebase</h2>
      </div>
      <p className="text-xs text-neutral-500 mb-5 max-w-2xl">
        Push notifications on your Customer &amp; Rider apps require Firebase.
        If you already run your own Firebase project, upload your App IDs and
        config files here so builds bundle them. Otherwise the platform
        auto-provisions Firebase apps for you — no action needed.
        <span className="block mt-1 text-neutral-400">
          <ShieldCheck className="w-3 h-3 inline mr-1" />
          Firebase Service-Account credentials are managed by Platform Admin only.
        </span>
      </p>

      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
        <SummaryPill label="Provisioned" value={`${status?.successful_count ?? 0}/4`}
                     tone={status?.complete ? "leaf" : "sand"} testid="fb-pill-provisioned" />
        <SummaryPill label="Auto" value={autoCount} tone="sky" testid="fb-pill-auto" />
        <SummaryPill label="Bring-your-own" value={byoCount} tone="clay" testid="fb-pill-byo" />
        <SummaryPill label="Service Account"
                     value={saPresent ? "Attached" : "None"}
                     tone={saPresent ? "leaf" : "neutral"}
                     testid="fb-pill-sa" />
      </div>

      {saMissingForByo && (
        <div className="mb-5 rounded-2xl border border-brand-clay/40 bg-brand-clay/5 p-4 flex items-start gap-3"
             data-testid="fb-warning-missing-sa">
          <AlertTriangle className="w-5 h-5 text-brand-clay shrink-0 mt-0.5" />
          <div className="text-xs text-neutral-700">
            You&apos;ve uploaded {byoCount} bring-your-own Firebase app{byoCount === 1 ? "" : "s"} but no
            matching <b>Firebase Service Account</b> is attached yet. Ask your
            Platform Admin to attach the SA for your Firebase project — until
            then push notifications from the server will be skipped.
          </div>
        </div>
      )}

      <div className="space-y-3">
        {SLOT_KEYS.map((k) => (
          <SlotRow
            key={k}
            slotKey={k}
            slot={status?.slots?.[k] || { state: "empty", source: null }}
            onChanged={load}
          />
        ))}
      </div>
    </section>
  );
}

function SummaryPill({ label, value, tone = "neutral", testid }) {
  const tones = {
    leaf: "bg-brand-leaf/10 text-brand-leaf border-brand-leaf/20",
    sand: "bg-brand-sand/15 text-brand-sand border-brand-sand/30",
    sky: "bg-brand-sky/10 text-brand-sky border-brand-sky/20",
    clay: "bg-brand-clay/10 text-brand-clay border-brand-clay/20",
    neutral: "bg-neutral-100 text-neutral-600 border-neutral-200",
  };
  return (
    <div className={`rounded-2xl border p-3 text-center ${tones[tone] || tones.neutral}`} data-testid={testid}>
      <div className="text-xl font-extrabold leading-none">{value}</div>
      <div className="text-[10px] uppercase tracking-wider font-bold mt-1 opacity-80">{label}</div>
    </div>
  );
}

function SlotRow({ slotKey, slot, onChanged }) {
  const platform = slotKey.endsWith("_android") ? "android" : "ios";
  const expectedFile = platform === "android" ? "google-services.json" : "GoogleService-Info.plist";
  const source = slot?.source;
  const state = slot?.state;

  const [expanded, setExpanded] = useState(false);
  const [appId, setAppId] = useState(slot?.app_id || "");
  const [identifier, setIdentifier] = useState(slot?.identifier || "");
  const [file, setFile] = useState(null);
  const [takeOwnership, setTakeOwnership] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Reset local state when the slot data changes (e.g. after refresh)
    setAppId(slot?.app_id || "");
    setIdentifier(slot?.identifier || "");
  }, [slot?.app_id, slot?.identifier]);

  const upload = async () => {
    if (!file) return toast.error(`Select the ${expectedFile}`);
    setSaving(true);
    try {
      const form = new FormData();
      // App ID / identifier are auto-parsed from the uploaded file when
      // left blank. Send them only if the user typed values (backend
      // then validates they match the file).
      if (appId.trim()) form.append("app_id", appId.trim());
      if (identifier.trim()) form.append("identifier", identifier.trim());
      form.append("config_file", file);
      if (takeOwnership) form.append("take_ownership", "true");
      await api.post(
        `/api/admin/firebase/apps/${slotKey}/manual`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success(`${SLOT_LABELS[slotKey]} saved`);
      setFile(null);
      setExpanded(false);
      setTakeOwnership(false);
      onChanged();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Upload failed";
      // If the backend refused an AUTO overwrite, auto-arm take_ownership
      // and let the user re-submit.
      if (String(detail).toLowerCase().includes("take_ownership") && !takeOwnership) {
        setTakeOwnership(true);
        toast.warning("This slot is auto-provisioned. Confirm the checkbox and retry.");
      } else {
        toast.error(detail);
      }
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!window.confirm(
      `Clear the bring-your-own Firebase config for ${SLOT_LABELS[slotKey]}? ` +
      `Your builds will lose push notifications on this app until you re-upload.`
    )) return;
    try {
      await api.delete(`/api/admin/firebase/apps/${slotKey}/manual`);
      toast.success(`Cleared ${SLOT_LABELS[slotKey]}`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed");
    }
  };

  return (
    <div className="border border-neutral-200 rounded-2xl p-3.5" data-testid={`fb-slot-${slotKey}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-bold truncate">{SLOT_LABELS[slotKey]}</span>
          <SlotBadge state={state} source={source} />
        </div>
        <div className="flex gap-1.5 shrink-0">
          {state === "ok" && source === "byo" && (
            <Button variant="outline" onClick={clear}
                    className="h-8 text-xs border-red-200 text-red-700 hover:bg-red-50"
                    data-testid={`fb-slot-clear-${slotKey}`}>
              <X className="w-3.5 h-3.5 mr-1"/> Clear BYO
            </Button>
          )}
          <Button variant="outline" onClick={() => setExpanded(!expanded)}
                  className="h-8 text-xs"
                  data-testid={`fb-slot-toggle-${slotKey}`}>
            {state === "empty" ? "Upload existing" : (expanded ? "Cancel" : "Replace")}
          </Button>
        </div>
      </div>
      {state === "ok" && (
        <div className="mt-1 text-[11px] font-mono text-neutral-500 truncate" title={`${slot?.app_id} · ${slot?.identifier || ""}`}>
          {slot?.app_id} {slot?.identifier ? `· ${slot.identifier}` : ""}
        </div>
      )}

      {expanded && (
        <div className="mt-3 space-y-2 pt-3 border-t border-neutral-100">
          <div>
            <label className="text-[11px] uppercase tracking-wider font-bold text-neutral-500">
              Firebase App ID <span className="text-neutral-400">(auto-filled from file if blank)</span>
            </label>
            <Input placeholder={platform === "android" ? "e.g. 1:12345:android:abc..." : "e.g. 1:12345:ios:abc..."}
                   value={appId} onChange={(e) => setAppId(e.target.value)}
                   className="h-10 rounded-xl mt-1"
                   data-testid={`fb-slot-app-id-${slotKey}`} />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider font-bold text-neutral-500">
              {platform === "android" ? "Package name" : "Bundle ID"} <span className="text-neutral-400">(optional)</span>
            </label>
            <Input placeholder={platform === "android" ? "e.g. com.acme.customer" : "e.g. com.acme.customer"}
                   value={identifier} onChange={(e) => setIdentifier(e.target.value)}
                   className="h-10 rounded-xl mt-1 font-mono text-xs"
                   data-testid={`fb-slot-identifier-${slotKey}`} />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider font-bold text-neutral-500">
              {expectedFile}
            </label>
            <div className="mt-1 rounded-xl border-2 border-dashed border-neutral-200 p-3 flex items-center gap-2">
              <input type="file"
                     accept={platform === "android" ? "application/json,.json" : ".plist,text/xml,application/xml"}
                     onChange={(e) => setFile(e.target.files?.[0] || null)}
                     className="text-xs"
                     data-testid={`fb-slot-file-${slotKey}`} />
              {file && <span className="text-[11px] text-neutral-500 truncate">{file.name}</span>}
            </div>
          </div>
          {source === "auto" && (
            <label className="flex items-start gap-2 p-3 rounded-xl bg-brand-clay/5 border border-brand-clay/20 cursor-pointer"
                   data-testid={`fb-slot-take-ownership-${slotKey}`}>
              <input type="checkbox" checked={takeOwnership}
                     onChange={(e) => setTakeOwnership(e.target.checked)}
                     className="mt-0.5 accent-brand-clay" />
              <span className="text-[11px] text-neutral-700">
                <b>Take ownership:</b> replace the auto-provisioned Firebase app with
                my own. The platform&apos;s Firebase project quota will drift by 1
                app until an admin runs Sync — this is normal.
              </span>
            </label>
          )}
          <Button onClick={upload} disabled={saving}
                  className="w-full h-10 rounded-xl bg-brand-sky hover:bg-blue-700 font-bold"
                  data-testid={`fb-slot-save-${slotKey}`}>
            <Upload className="w-4 h-4 mr-2" />
            {saving ? "Uploading…" : (state === "empty" ? "Upload" : "Replace")}
          </Button>
        </div>
      )}
    </div>
  );
}

function SlotBadge({ state, source }) {
  if (state === "ok" && source === "byo") {
    return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">BYO</span>;
  }
  if (state === "ok" && source === "auto") {
    return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">AUTO</span>;
  }
  if (state === "error") {
    return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">ERROR</span>;
  }
  return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-500">EMPTY</span>;
}
